import styled from '@emotion/styled';
import { FC } from 'react';

import { FontFamily } from '@/enums/index';
import { StringParam, useQueryParams } from '@/hooks/useQueryParams';
import { useResource } from '@/lib/datx-jsonapi-react';
import { getNewsArticleById } from '@/queries/news-articles';
import { BREAKPOINTS, MAX_CONTENT_WIDTH } from '@/styles/themes/default';
import { IThemeProps } from '@/styles/themes/types';
import { dateToString } from '@/utils/date';
import { splitIdAndSlug } from '@/utils/helperFunctions';

import { Container } from '../core/Grid';

const Wrapper = styled.div<IThemeProps>`
  margin: 40px 0 80px;
  text-align: center;
`;

const PublishedDate = styled.div<IThemeProps>`
  font-size: 16px;
  line-height: 24px;
  color: ${({ theme }) => theme.legacy.colors.lightText};
  font-family: ${({ theme }) => theme.legacy.typography.fontFamily[FontFamily.IBMPlexSans]};
`;

const Title = styled.h1<IThemeProps>`
  color: black;
  font-size: 40px;
  line-height: 48px;
  font-family: ${({ theme }) => theme.legacy.typography.fontFamily[FontFamily.SpaceGrotesk]};
  padding: 16px 20px 32px;

  @media (min-width: ${BREAKPOINTS.Desktop.min}px) {
    font-size: 64px;
    line-height: 80px;
    padding: 16px 50px 40px;
  }
`;

const Author = styled.div<IThemeProps>`
  font-size: 16px;
  line-height: 24px;
  margin: 0 0 14px;
  color: ${({ theme }) => theme.legacy.colors.lightText};
  font-family: ${({ theme }) => theme.legacy.typography.fontFamily[FontFamily.IBMPlexSans]};

  span {
    margin-left: 15px;
  }
`;

const FeaturedImage = styled.img<IThemeProps>`
  max-width: 100%;
  height: auto;
  margin: 68px auto 40px;

  @media (min-width: ${BREAKPOINTS.Desktop.min}px) {
    width: 100%;
    max-width: ${MAX_CONTENT_WIDTH}px;
    padding: 0 10px;
  }
`;

const ArticleAuthorName = styled.span<IThemeProps>`
  color: ${({ theme }) => theme.legacy.colors.brand.primary};
  font-family: ${FontFamily.SpaceGrotesk};
  font-weight: bold;
  margin-right: 16px;
  font-size: 16px;
  line-height: 24px;
`;

export const Header: FC = () => {
  const { id: articleSlug } = useQueryParams<{ id: string }>({
    id: StringParam,
  });

  const { id: articleId } = splitIdAndSlug(articleSlug);

  const { data: article } = useResource(getNewsArticleById(articleId));
  const readTimeLabel = `${article.readTime === '1' ? 'minute' : 'minutes'} read`;

  return (
    <Wrapper>
      <Container>
        <PublishedDate>{dateToString(article.publishedOn, 'MMMM do, yyyy')}</PublishedDate>
        <Title>{article.title}</Title>
        <Author>
          <ArticleAuthorName>{article.author}</ArticleAuthorName>
          {article.readTime && (
            <span>
              {article.readTime} {readTimeLabel}
            </span>
          )}
        </Author>
      </Container>
      <FeaturedImage src={article.largeFeaturedImageUrl} alt={`photpo ${article.title}`} />
    </Wrapper>
  );
};
